import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";

import { agenciesGet } from "@/store/agencies";
import { usersGetAll, usersUpdateUser } from "@/store/users";
import { Agency, User } from '@/types';

import AppModal from "@/components/AppModal";
import AppText from "@/components/AppText";
import AppBox from "@/components/AppBox";
import AppButton from "@/components/AppButton";
import AgencyModal from "@/components/Modals/AgencyModal";
import AgenciesTable from "@/components/Tables/AgenciesTable";
import AddUserToAgencyModal from "@/components/Modals/AddUserToAgencyModal";
import AppPageContainer from "@/components/AppPageContainer";

const Agencies: React.FC = () => {
  const dispatch = useAppDispatch();
  const { agencies } = useAppSelector((state) => state.agencies);
  const { users } = useAppSelector((state) => state.users);

  const [showAgencyModal, setShowAgencyModal] = useState<boolean>(false);
  const [showAddUserModal, setShowAddUserModal] = useState<boolean>(false);
  const [selectedAgency, setSelectedAgency] = useState<Agency | null>(null);

  // Get available users (not in any agency) and sort by creation date
  const availableUsers = users
    .filter(user => !user.deleted_at && !user.agency_id)
    .sort((a, b) => {
      // Sort by created_at in descending order (newer first)
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

  useEffect(() => {
    dispatch(agenciesGet());
    dispatch(usersGetAll());
  }, [dispatch]);

  const handleToggleAgencyModal = () => setShowAgencyModal((prev) => !prev);

  const handleToggleAddUserModal = (agency?: Agency) => {
    if (agency) {
      setSelectedAgency(agency);
    }
    setShowAddUserModal((prev) => !prev);
  };

  const handleAddUserToAgency = async (user: User) => {
    if (selectedAgency) {
      await dispatch(usersUpdateUser({ 
        id: user.id, 
        data: { agency_id: selectedAgency.id } 
      }));
      
      // Refresh data
      await dispatch(agenciesGet());
      await dispatch(usersGetAll());
      
      handleToggleAddUserModal();
    }
  };

  const handleFetchAgencies = async () => {
    handleToggleAgencyModal();
    await dispatch(agenciesGet());
  }

  return (
    <AppPageContainer
      title="Agencies"
      // titleAdorment={
      //   <AppBox width={220}>
      //     <AppButton onClick={handleToggleAgencyModal}>Add Agency</AppButton>
      //   </AppBox>
      // }
    >
      <AgenciesTable
        tableRows={agencies}
        onAddUser={handleToggleAddUserModal}
      />

      <AppModal isOpen={showAgencyModal} title="Add Agency" onClose={handleToggleAgencyModal}>
        <AgencyModal onSaveAgency={handleFetchAgencies} />
      </AppModal>

      {selectedAgency && (
        <AddUserToAgencyModal
          isOpen={showAddUserModal}
          agency={selectedAgency}
          users={availableUsers}
          onClose={() => handleToggleAddUserModal()}
          onSave={handleAddUserToAgency}
        />
      )}
    </AppPageContainer>
  );
};

export default Agencies; 